import React, { useState, useEffect, useRef } from "react";
import Navbar from "../../components/common/navbar";
import { useNavigate, Link } from "react-router-dom";
import constant from "../../constant";
import { Info, Download, Search } from "lucide-react";
import PatientModal from "./ui/patientModal";
import MedicineReminder from "./ui/MedicineReminder";
import MedicineModal from "./ui/medicineModal";
import DischargeModal from "./ui/dischargeModal";
import PatientAction from "./ui/patientAction";
import { database } from "../../firebase";
import { ref, onValue, query, limitToLast, set } from "firebase/database";
import Alerts from "./ui/alerts";

const calculateMedian = (arr) => {
  if (!arr || arr.length === 0) return "-";

  const sorted = [...arr].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  let median;
  if (sorted.length % 2 === 0) {
    median = (sorted[middle - 1] + sorted[middle]) / 2;
  } else {
    median = sorted[middle];
  }

  return median.toFixed(2);
};

const getVitalSignColor = (key, value) => {
  if (value === "-") return "text-gray-500";
  switch (key) {
    case "heartRate":
      if (value >= 60 && value <= 100) return "text-green-500";
      if ((value >= 50 && value <= 59) || (value >= 101 && value <= 120))
        return "text-yellow-500";
      if (value < 50 || value > 120) return "text-red-500";
      break;
    case "respirationRate":
      if (value >= 12 && value <= 20) return "text-green-500";
      if ((value >= 10 && value <= 11) || (value >= 21 && value <= 24))
        return "text-yellow-500";
      if (value < 10 || value > 24) return "text-red-500";
      break;
    case "nibp":
      const [systolic] = value.toString().split("/").map(Number);
      if (systolic >= 90 && systolic <= 120) return "text-green-500";
      if (systolic >= 121 && systolic <= 139) return "text-yellow-500";
      if (systolic < 90 || systolic > 139) return "text-red-500";
      break;
    case "spo2":
      if (value >= 95 && value <= 100) return "text-green-500";
      if (value >= 90 && value <= 94) return "text-yellow-500";
      if (value < 90) return "text-red-500";
      break;
    case "temperature":
      if (value >= 36.1 && value <= 37.5) return "text-green-500";
      if ((value >= 35.5 && value <= 36.0) || (value >= 37.6 && value <= 38.0))
        return "text-yellow-500";
      if (value < 35.5 || value > 38.0) return "text-red-500";
      break;
    default:
      return "text-gray-500";
  }
};

const DashboardContent = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [hospId, setHospId] = useState('');
  const [hospitalMedicineTime, setHospitalMedicineTime] = useState(null);
  const [medicineReminder, setShowMedicineReminder] = useState({
    show: false,
    lastVisibleTime: null,
    message: "",
  });
  const timeDisplayMessage = {
    after_Dinner: "after Dinner Medicine",
    after_breakfast: "after Breakfast Medicine",
    after_lunch: "after Lunch Medicine",
    before_breakfast: "before Breakfast Medicine",
    before_dinner: "before Dinner Medicine",
    before_lunch: "before Lunch Medicine",

  }

  const [patients, setPatients] = useState([]);
  const [vitals, setVitals] = useState({});

  const [lastUpdated, setLastUpdated] = useState(null); // New state for last updated timestamp
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMedicineLoading, setIsMedicineLoading] = useState(false);
  const [isDischargeLoading, setIsDischargeLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const hospitalId = localStorage.getItem("hospitalId");

        setHospId(localStorage.getItem("hospitalId"))

        if (!token || !hospitalId) {
          alert("No authentication token or hospital ID found");
          navigate("/login");
          return;
        }

        // Fetch dashboard data
        const dashboardUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/dashboard`;
        const dashboardResponse = await fetch(dashboardUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!dashboardResponse.ok) {
          const message = await dashboardResponse.text();
          throw new Error(message || "Failed to fetch dashboard data");
        }

        const dashboardResult = await dashboardResponse.json();
        setDashboardData(dashboardResult);
        setHospitalMedicineTime(dashboardResult.hospital.medicine_time || {});


        // Fetch patient data
        const patientUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/`;
        const patientResponse = await fetch(patientUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!patientResponse.ok) {
          const message = await patientResponse.text();
          throw new Error(message || "Failed to fetch patient data");
        }

        const patientResult = await patientResponse.json();
        const transformedPatients = patientResult.map((patient) => ({
          patientId: patient.patient_id,
          name: patient.name,
          status: patient.current_status || "-",
          patientName: patient.name,
          id: patient.patient_hospital_id,
          hospitalId: patient.hospital_id,
          bedNumber: patient.bed_number,
          heartRate: "-",
          respirationRate: "-",
          nibp: "-",
          spo2: "-",
          temperature: "-",
          ecg: "-",
        }));

        setPatients(transformedPatients);
      } catch (error) {
        setError(error.message || "An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (patients.length === 0) return;

    const unsubscribers = [];
    const vitalDataStore = {};

    const updateVitals = () => {
      const now = Date.now();
      const delayMinutes = 2 * 60 * 1000;

      const updatedVitals = {};
      for (const patientId in vitalDataStore) {
        updatedVitals[patientId] = {};
        for (const vitalKey in vitalDataStore[patientId]) {
          const { value, time } = vitalDataStore[patientId][vitalKey];

          updatedVitals[patientId][vitalKey] =
            now - time <= delayMinutes ? value : "-";
        }
      }

      setVitals(updatedVitals);
      setLastUpdated(new Date().toISOString());

      for (let medicineTimeKey in hospitalMedicineTime) {
        const currentTime = Date.now();
        const timeString = hospitalMedicineTime[medicineTimeKey];
        const [hours, minutes] = timeString.split(":").map(Number);
        const medicineTime = new Date();
        medicineTime.setHours(hours, minutes, 0, 0);

        const timeDiff = medicineTime.getTime() - currentTime;
        const oneMin = 60 * 1000;
        const fiveMin = 5 * 60 * 1000;

        const isWithinFiveMinBefore = timeDiff >= 0 && timeDiff <= fiveMin;

        if (isWithinFiveMinBefore) {
          const lastVisible = medicineReminder?.lastVisibleTime ?? null;
          const shouldShowByLastVisible = !lastVisible || currentTime - lastVisible >= oneMin;

          if (shouldShowByLastVisible) {
            setShowMedicineReminder({
              show: true,
              lastVisibleTime: currentTime,
              message: timeDisplayMessage[medicineTimeKey] || medicineTimeKey,
            });
          }
        } else {
          // Reset lastVisibleTime when outside the 5-minute window
          if (medicineReminder.lastVisibleTime !== null) {
            setShowMedicineReminder(prev => ({
              ...prev,
              show: false,
              lastVisibleTime: null,
            }));
          }
        }
      }
    };

    patients.forEach((patient) => {
      const { hospitalId, bedNumber, patientId } = patient;
      if (!hospitalId || !bedNumber) return;

      const vitalKeys = {
        hr: "heartRate",
        resp_rate: "respirationRate",
        nibp: "nibp",
        spo2: "spo2",
        temp: "temperature",
      };

      Object.entries(vitalKeys).forEach(([firebaseKey, stateKey]) => {
        const vitalRef = ref(
          database,
          `hospital/${hospitalId}/${bedNumber}/${firebaseKey}`
        );
        const unsubscribe = onValue(vitalRef, (snapshot) => {
          const data = snapshot.val();
          if (!data) return;

          const entries = Object.values(data);
          const latestEntry = entries.sort((a, b) => b.time - a.time)[0];
          if (!latestEntry) return;

          if (!vitalDataStore[patientId]) vitalDataStore[patientId] = {};
          vitalDataStore[patientId][stateKey] = {
            value: latestEntry.value,
            time: latestEntry.time * 1000,
          };

          updateVitals();
        });

        unsubscribers.push(unsubscribe);
      });
    });

    // Periodically check if data expired (older than 20 min)
    const interval = setInterval(updateVitals, 10 * 1000); // every 10s refresh

    return () => {
      unsubscribers.forEach((u) => u());
      clearInterval(interval);
    };
  }, [patients]);

  const filteredDevices = patients.filter((device) =>
    device.patientName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
  const [isDischargeModalOpen, setIsDischargeModalOpen] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [patientFormData, setPatientFormData] = useState({
    patientId: "",
    patientName: "",
    patientAge: "",
    admittedOn: "",
  });
  const [medicineFormData, setMedicineFormData] = useState({
    morning: false,
    morning_comment: "",
    morning_time: null,
    afternoon: false,
    afternoon_comment: "",
    afternoon_time: null,
    night: false,
    night_comment: "",
    night_time: null,
    schedule_id: "",
  });
  const [dischargeFormData, setDischargeFormData] = useState({
    current_status: "discharged",
    discharge_reason: "",
    discharged_on: "",
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  const handlePatientClick = (device) => {
    setCurrentPatient(device);
    setPatientFormData({
      patientId: device.patientId,
      patientName: device.patientName,
      patientAge: "",
      admittedOn: "",
    });
    setIsPatientModalOpen(true);
  };

  const handleMedicineClick = async (device) => {
    setCurrentPatient(device);
    setIsMedicineLoading(true);
    try {
      const token = localStorage.getItem("token");
      const hospitalId = localStorage.getItem("hospitalId");
      const apiUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${device.patientId}/medicine`;

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Failed to fetch medicine schedule");
      }

      const data = await response.json();
      setMedicineFormData({
        morning: data.morning || false,
        morning_comment: data.morning_comment || "",
        morning_time: data.morning_time || null,
        afternoon: data.afternoon || false,
        afternoon_comment: data.afternoon_comment || "",
        afternoon_time: data.afternoon_time || null,
        night: data.night || false,
        night_comment: data.night_comment || "",
        night_time: data.night_time || null,
        schedule_id: data.schedule_id || "",
      });
      setIsMedicineModalOpen(true);
    } catch (error) {
      alert(
        error.message || "An error occurred while fetching medicine schedule"
      );
    } finally {
      setIsMedicineLoading(false);
    }
  };

  const handleDischargeClick = (device) => {
    setCurrentPatient(device);
    setDischargeFormData({
      current_status: "discharged",
      discharge_reason: "",
      discharged_on: new Date().toISOString().split("T")[0],
    });
    setIsDischargeModalOpen(true);
  };

  const handleNameClick = (e, device) => {
    e.stopPropagation();
    setCurrentPatient(device);
    const rect = e.target.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setIsPopupOpen(true);
  };

  const handleClickOutside = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setIsPopupOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePatientSubmit = (e) => {
    e.preventDefault();
    setIsPatientModalOpen(false);
    alert(`Patient ${patientFormData.patientName} updated successfully!`);
  };

  const handleMedicineSubmit = async (e) => {
    e.preventDefault();
    setIsMedicineLoading(true);
    try {
      const token = localStorage.getItem("token");
      const hospitalId = localStorage.getItem("hospitalId");
      if (!token || !hospitalId || !currentPatient?.patientId) {
        alert("Missing required information for medicine submission");
        return;
      }

      const getLocalIsoTime = () => {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        return (
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
          `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        );
      };

      const currentTime = getLocalIsoTime();
      const updatedFormData = {
        ...medicineFormData,
        morning_time:
          medicineFormData.morning && !medicineFormData.morning_time
            ? currentTime
            : medicineFormData.morning_time,
        afternoon_time:
          medicineFormData.afternoon && !medicineFormData.afternoon_time
            ? currentTime
            : medicineFormData.afternoon_time,
        night_time:
          medicineFormData.night && !medicineFormData.night_time
            ? currentTime
            : medicineFormData.night_time,
      };

      const apiUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${currentPatient.patientId}/medicine`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedFormData),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Failed to submit medicine data");
      }

      setIsMedicineModalOpen(false);
      setToast({
        show: true,
        message: `Medicine schedule for ${currentPatient.patientName} updated successfully!`,
      });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    } catch (error) {
      alert(
        error.message || "An error occurred while submitting medicine data"
      );
    } finally {
      setIsMedicineLoading(false);
    }
  };

  const handleDischargeSubmit = async (e) => {
    e.preventDefault();
    setIsDischargeLoading(true);
    try {
      const token = localStorage.getItem("token");
      const hospitalId = localStorage.getItem("hospitalId");

      if (!token || !hospitalId || !currentPatient?.patientId) {
        alert("Missing required information for discharge submission");
        return;
      }

      const apiUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${currentPatient.patientId}`;

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dischargeFormData),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Failed to discharge patient");
      }

      setIsDischargeModalOpen(false);
      setToast({
        show: true,
        message: `Patient ${currentPatient.patientName} discharged successfully!`,
      });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);

      // Refresh patient list
      const patientUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/`;
      const patientResponse = await fetch(patientUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (patientResponse.ok) {
        const patientResult = await patientResponse.json();
        const transformedPatients = patientResult.map((patient) => ({
          patientId: patient.patient_id,
          name: patient.name,
          status: patient.current_status || "-",
          patientName: patient.name,
          id: patient.patient_hospital_id,
          hospitalId: patient.hospital_id,
          bedNumber: patient.bed_number,
          heartRate: "-",
          respirationRate: "-",
          nibp: "-",
          spo2: "-",
          temperature: "-",
          ecg: "-",
        }));
        setPatients(transformedPatients);
      }
    } catch (error) {
      alert(error.message || "An error occurred while discharging patient");
    } finally {
      setIsDischargeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-yellow-500 font-bold text-2xl text-center h-screen uppercase flex justify-center items-center">
        {JSON.parse(error).msg}
      </div>
    );
  }

  return (
    <>
      {medicineReminder.show && hospitalMedicineTime && (
        <MedicineReminder slot={medicineReminder.message} />
      )}
      {toast.show && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-green-500 text-white p-8 rounded-md shadow-lg animate-fade-in-out">
            {toast.message}
          </div>
        </div>
      )}

      <div className="px-6 pt-20 md:pt-4 pb-8">
        <Navbar
          admitted_patients={dashboardData?.stats?.admitted_patients || 0}
          assigned_devices={dashboardData?.stats?.disconnected_devices || 0}
          discharged_patients={dashboardData?.stats?.discharged_today || 0}
          title={dashboardData?.hospital?.logo || ""}
          lastUpdated={lastUpdated} // Pass lastUpdated to Navbar
        />

        <div className="my-8 lg:px-4 flex flex-col lg:flex-row gap-4">
          <div className="flex-1 lg:basis-10/12">
            <div>
              <div className="mb-8">
                <div className="flex justify-between items-center mb-6">
                  <div className="relative w-full sm:w-96">
                    <Search
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="text"
                      placeholder="Search patients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-xl overflow-x-auto border border-gray-100">
                {filteredDevices.length === 0 ? (
                  <>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Patient Name</div>
                              <div className="block text-center font-bold">
                                {" "}
                                & ID
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Heart Rate </div>
                              <div className="block text-center font-normal">
                                (bpm)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Respiration Rate</div>
                              <div className="block text-center font-normal">
                                (rpm)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">NiBP</div>
                              <div className="block text-center font-normal">
                                (mmHg)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">SpO2</div>
                              <div className="block text-center font-normal">
                                (%)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Temperature</div>
                              <div className="block text-center font-normal">
                                (°C)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            Actions
                          </th>
                        </tr>
                      </thead>
                    </table>
                    <div className="text-center py-8 text-gray-500 text-lg">
                      No patients found
                    </div>
                  </>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Patient Name</div>
                              <div className="block text-center font-bold">
                                {" "}
                                & ID
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Heart Rate </div>
                              <div className="block text-center font-normal">
                                (bpm)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Respiration Rate</div>
                              <div className="block text-center font-normal">
                                (rpm)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">NiBP</div>
                              <div className="block text-center font-normal">
                                (mmHg)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">SpO2</div>
                              <div className="block text-center font-normal">
                                (%)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            <div className="grid grid-cols-1 text-center">
                              <div className="">Temperature</div>
                              <div className="block text-center font-normal">
                                (°C)
                              </div>
                            </div>
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-bold text-gray-500 tracking-wider border-b-2 border-gray-200">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDevices.map((device, index) => (
                          <tr
                            key={device.patientId}
                            className={`
                            hover:bg-blue-50 cursor-pointer transition-colors duration-150
                            ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                          `}
                          // onClick={() => handlePatientClick(device)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                              <div className="flex items-center">
                                <div>
                                  <button
                                    onClick={(e) => handleNameClick(e, device)}
                                    className="text-sm font-medium text-blue-600 hover:underline capitalize"
                                  >
                                    {device.patientName}
                                  </button>
                                  <div>{device.id}</div>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor(
                                "heartRate",
                                vitals[device.patientId]?.heartRate ||
                                device.heartRate
                              )}`}
                            >
                              {isNaN(
                                vitals[device.patientId]?.heartRate ||
                                device.heartRate
                              )
                                ? "-"
                                : Math.round(
                                  vitals[device.patientId]?.heartRate ||
                                  device.heartRate
                                )}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor(
                                "respirationRate",
                                vitals[device.patientId]?.respirationRate ||
                                device.respirationRate
                              )}`}
                            >
                              {isNaN(
                                vitals[device.patientId]?.respirationRate ||
                                device.respirationRate
                              )
                                ? "-"
                                : Math.round(
                                  vitals[device.patientId]?.respirationRate ||
                                  device.respirationRate
                                )}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor(
                                "nibp",
                                vitals[device.patientId]?.nibp || device.nibp
                              )}`}
                            >
                              {vitals[device.patientId]?.nibp ||
                                device.nibp ||
                                "-"}
                            </td>
                            {/* <td className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor('nibp', vitals[device.patientId]?.nibp || device.nibp)}`}>
                              {isNaN(vitals[device.patientId]?.nibp || device.nibp) ? '-' : Math.round(vitals[device.patientId]?.nibp || device.nibp)}
                            </td> */}
                            <td
                              className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor(
                                "spo2",
                                vitals[device.patientId]?.spo2 || device.spo2
                              )}`}
                            >
                              {isNaN(
                                vitals[device.patientId]?.spo2 || device.spo2
                              )
                                ? "-"
                                : Math.round(
                                  vitals[device.patientId]?.spo2 ||
                                  device.spo2
                                )}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap font-bold text-md text-center font-mono ${getVitalSignColor(
                                "temperature",
                                vitals[device.patientId]?.temperature ||
                                device.temperature
                              )}`}
                            >
                              {vitals[device.patientId]?.temperature ||
                                device.temperature}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                              <div className="flex items-center space-x-3">
                                <div className="relative group">
                                  <Link
                                    to={`/onboarded/patients/${device.patientId}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                                  >
                                    <Info size={18} />
                                  </Link>
                                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                    <div className="bg-gray-900 text-white text-xs rounded-md px-2 py-1 whitespace-nowrap">
                                      Patient Info
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                                <div className="relative group">
                                  <Link
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMedicineClick(device);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                                  >
                                    💊
                                  </Link>
                                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                    <div className="bg-gray-900 text-white text-xs rounded-md px-2 py-1 whitespace-nowrap">
                                      MediScheduler
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                                {/* <div className="relative group">
                                <Link
                                  to={`#`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  className="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                                >
                                  <Download size={18} />
                                </Link>
                                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-100">
                                  <div className="bg-gray-900 text-white text-xs rounded-md px-2 py-1 whitespace-nowrap">
                                    Download Report
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              </div> */}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <PatientModal
              isOpen={isPatientModalOpen}
              onClose={() => setIsPatientModalOpen(false)}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              onSubmit={handlePatientSubmit}
            />

            <MedicineModal
              isOpen={isMedicineModalOpen}
              onClose={() => setIsMedicineModalOpen(false)}
              isLoading={isMedicineLoading}
              medicineFormData={medicineFormData}
              setMedicineFormData={setMedicineFormData}
              onSubmit={handleMedicineSubmit}
            />

            <DischargeModal
              isOpen={isDischargeModalOpen}
              onClose={() => setIsDischargeModalOpen(false)}
              isLoading={isDischargeLoading}
              dischargeFormData={dischargeFormData}
              setDischargeFormData={setDischargeFormData}
              onSubmit={handleDischargeSubmit}
            />

            <PatientAction
              isOpen={isPopupOpen}
              currentPatient={currentPatient}
              popupPosition={popupPosition}
              onClose={() => setIsPopupOpen(false)}
              onDischargeClick={handleDischargeClick}
              popupRef={popupRef}
            />
          </div>

          <div className="flex-1 lg:basis-2/12 no-print">
            <Alerts hospitalId={hospId} />
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardContent;
